import React, { useState } from 'react';

import classNames from 'classnames';
import PropTypes from 'prop-types';
import { FaFacebookF, FaTelegramPlane, FaInstagram } from 'react-icons/fa';

import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router-dom';

import logo from '../../Assets/img/logo.webp';
import WithBackground from '../Common/WithBackground';

import './style.css';

const Header = ({ bg, overlay = 'dark' }) => {
  const { t } = useTranslation();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const location = useLocation();

  const toggleMenu = () => setIsMenuOpen(!isMenuOpen);
  const closeMenu = () => setIsMenuOpen(false);

  // Get current language from URL or i18n
  const getCurrentLanguage = () => {
    const pathSegments = location.pathname.split('/').filter(Boolean);
    const supportedLangs = ['pl', 'rus', 'ua', 'en'];
    return supportedLangs.includes(pathSegments[0]) ? pathSegments[0] : 'pl';
  };

  // Generate language-specific routes
  const getLocalizedRoute = (route) => {
    const currentLang = getCurrentLanguage();

    // Route mapping to match App.jsx routes exactly
    const routeMap = {
      pl: {
        '/': '/',
        '/oferty-pracy': '/oferty-pracy',
        '/dla-pracownika': '/dla-pracownika',
        '/dla-pracodawcy': '/dla-pracodawcy',
        '/o-nas': '/o-nas',
        '/nasz-zespol': '/nasz-zespol',
        '/partnerzy': '/partnerzy',
        '/opinie': '/opinie',
        '/kontakt': '/kontakt',
      },
      rus: {
        '/': '/rus',
        '/oferty-pracy': '/rus/vakansii',
        '/dla-pracownika': '/rus/dla-sotrudnika',
        '/dla-pracodawcy': '/rus/dla-rabotodatelya',
        '/o-nas': '/rus/o-nas',
        '/nasz-zespol': '/rus/nasha-komanda',
        '/partnerzy': '/rus/partnery',
        '/opinie': '/rus/otzyvy',
        '/kontakt': '/rus/kontakt',
      },
      ua: {
        '/': '/ua',
        '/oferty-pracy': '/ua/vakansii',
        '/dla-pracownika': '/ua/dlya-pratsivnika',
        '/dla-pracodawcy': '/ua/dlya-robotodavtsya',
        '/o-nas': '/ua/pro-nas',
        '/nasz-zespol': '/ua/nasha-komanda',
        '/partnerzy': '/ua/partneri',
        '/opinie': '/ua/vidhuky',
        '/kontakt': '/ua/kontakt',
      },
      en: {
        '/': '/en',
        '/oferty-pracy': '/en/job-offers',
        '/dla-pracownika': '/en/for-workers',
        '/dla-pracodawcy': '/en/for-employers',
        '/o-nas': '/en/about-us',
        '/nasz-zespol': '/en/our-team',
        '/partnerzy': '/en/partners',
        '/opinie': '/en/reviews',
        '/kontakt': '/en/contact',
      },
    };

    return routeMap[currentLang]?.[route] || route;
  };

  const headerContent = (
    <header className='header'>
      <div className='container'>
        <div className='header__content'>
          <div className='header__logo'>
            <Link
              to={getLocalizedRoute('/')}
              className='header__logo-link'
              onClick={closeMenu}
            >
              <img
                src={logo}
                alt='OpusA.P.T Logo'
                className='header__logo-image'
              />
              <div className='header__logo-wrapper'>
                <span className='header__logo-text' >
                  OPUS <span className='header__logo-accent'>HR</span>
                </span>
                <span className='header__logo-subtitle'>
                  agencja pracy tymczasowej
                </span>
              </div>
            </Link>
          </div>

          <nav className='header__nav'>
            <ul className='header__nav-list'>
              <li className='header__nav-item'>
                <Link
                  to={getLocalizedRoute('/')}
                  className='header__nav-link'
                  onClick={closeMenu}
                >
                  {t('navigation.home')}
                </Link>
              </li>
              <li className='header__nav-item'>
                <Link
                  to={getLocalizedRoute('/oferty-pracy')}
                  className='header__nav-link'
                  onClick={closeMenu}
                >
                  {t('navigation.vacancies')}
                </Link>
              </li>
              <li className='header__nav-item'>
                <Link
                  to={getLocalizedRoute('/o-nas')}
                  className='header__nav-link'
                  onClick={closeMenu}
                >
                  {t('navigation.aboutUs')}
                </Link>
              </li>
              <li className='header__nav-item'>
                <Link
                  to={getLocalizedRoute('/nasz-zespol')}
                  className='header__nav-link'
                  onClick={closeMenu}
                >
                  {t('navigation.ourTeam')}
                </Link>
              </li>
              <li className='header__nav-item'>
                <Link
                  to={getLocalizedRoute('/partnerzy')}
                  className='header__nav-link'
                  onClick={closeMenu}
                >
                  {t('navigation.partners')}
                </Link>
              </li>
              <li className='header__nav-item'>
                <Link
                  to={getLocalizedRoute('/opinie')}
                  className='header__nav-link'
                  onClick={closeMenu}
                >
                  {t('navigation.reviews')}
                </Link>
              </li>
              <li className='header__nav-item'>
                <Link
                  to={getLocalizedRoute('/kontakt')}
                  className='header__nav-link'
                  onClick={closeMenu}
                >
                  {t('navigation.contact')}
                </Link>
              </li>
            </ul>
          </nav>

          <div className='header__social'>
            <a
              href='https://www.facebook.com/opusapt'
              className='header__social-link'
              aria-label='Facebook'
              target='_blank'
              rel='noopener noreferrer'
            >
              <FaFacebookF />
            </a>
            <a
              href='https://t.me/opusapt_agencjapracy'
              className='header__social-link'
              aria-label='Telegram'
              target='_blank'
              rel='noopener noreferrer'
            >
              <FaTelegramPlane />
            </a>
            <a
              href='https://www.instagram.com/opus.apt'
              className='header__social-link'
              aria-label='Instagram'
              target='_blank'
              rel='noopener noreferrer'
            >
              <FaInstagram />
            </a>
          </div>

          <button
            className={classNames('header__burger', {
              'header__burger--active': isMenuOpen,
            })}
            aria-label='Toggle menu'
            aria-expanded={isMenuOpen}
            onClick={toggleMenu}
          >
            <span className='header__burger-line'></span>
            <span className='header__burger-line'></span>
            <span className='header__burger-line'></span>
          </button>
        </div>
      </div>

      <div
        className={classNames('header__mobile-overlay', {
          'header__mobile-overlay--active': isMenuOpen,
        })}
        aria-hidden={!isMenuOpen}
      >
        <nav className='header__mobile-nav'>
          <ul className='header__mobile-nav-list'>
            <li className='header__mobile-nav-item'>
              <Link
                to={getLocalizedRoute('/')}
                className='header__mobile-nav-link'
                onClick={closeMenu}
              >
                {t('navigation.home')}
              </Link>
            </li>
            <li className='header__mobile-nav-item'>
              <Link
                to={getLocalizedRoute('/oferty-pracy')}
                className='header__mobile-nav-link'
                onClick={closeMenu}
              >
                {t('navigation.vacancies')}
              </Link>
            </li>
            <li className='header__mobile-nav-item'>
              <Link
                to={getLocalizedRoute('/o-nas')}
                className='header__mobile-nav-link'
                onClick={closeMenu}
              >
                {t('navigation.aboutUs')}
              </Link>
            </li>
            <li className='header__mobile-nav-item'>
              <Link
                to={getLocalizedRoute('/nasz-zespol')}
                className='header__mobile-nav-link'
                onClick={closeMenu}
              >
                {t('navigation.ourTeam')}
              </Link>
            </li>
            <li className='header__mobile-nav-item'>
              <Link
                to={getLocalizedRoute('/partnerzy')}
                className='header__mobile-nav-link'
                onClick={closeMenu}
              >
                {t('navigation.partners')}
              </Link>
            </li>
            <li className='header__mobile-nav-item'>
              <Link
                to={getLocalizedRoute('/opinie')}
                className='header__mobile-nav-link'
                onClick={closeMenu}
              >
                {t('navigation.reviews')}
              </Link>
            </li>
            <li className='header__mobile-nav-item'>
              <Link
                to={getLocalizedRoute('/kontakt')}
                className='header__mobile-nav-link'
                onClick={closeMenu}
              >
                {t('navigation.contact')}
              </Link>
            </li>
          </ul>

          <div className='header__mobile-social'>
            <a
              href='https://www.facebook.com/opusapt'
              className='header__mobile-social-link'
              aria-label='Facebook'
              target='_blank'
              rel='noopener noreferrer'
            >
              <FaFacebookF />
            </a>
            <a
              href='https://t.me/opusapt_agencjapracy'
              className='header__mobile-social-link'
              aria-label='Telegram'
              target='_blank'
              rel='noopener noreferrer'
            >
              <FaTelegramPlane />
            </a>
            <a
              href='https://www.instagram.com/opus.apt'
              className='header__mobile-social-link'
              aria-label='Instagram'
              target='_blank'
              rel='noopener noreferrer'
            >
              <FaInstagram />
            </a>
          </div>
        </nav>
      </div>
    </header>
  );

  return bg ? (
    <WithBackground bg={bg} overlay={overlay}>
      {headerContent}
    </WithBackground>
  ) : (
    headerContent
  );
};

Header.propTypes = {
  bg: PropTypes.string,
  overlay: PropTypes.oneOf(['dark', 'light', 'golden', 'none']),
};

Header.defaultProps = {
  bg: null,
  overlay: 'dark',
};

export default Header;
